import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useCategories } from '../hooks/useCategories';
import { useContentText } from '../context/ContentContext';

const Categories = () => {
  const containerRef = useRef(null);
  const { items: cats, loading, error, retry } = useCategories();
  const eyebrow = useContentText('home.categories.eyebrow');
  const title = useContentText('home.categories.title');
  const titleAccent = useContentText('home.categories.title_accent');
  const subtitle = useContentText('home.categories.subtitle');

  useEffect(() => {
    const els = containerRef.current?.querySelectorAll('.reveal');
    if (!els) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in-view'); });
    }, { threshold: 0.1 });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [cats]);

  // While the category API is still resolving, reserve the section height
  // with a light skeleton grid so the browser paints predictable layout
  // instead of promoting "Our Story" to the top of the fold. If the fetch
  // fails outright, surface a friendly retry state so mobile visitors on
  // flaky networks are never left staring at pale rectangles.
  if (loading || (error && !cats.length)) {
    return (
      <section id="categories" className="relative py-10 md:py-24 bg-[#FDF3EB]" data-testid="home-categories-loading" aria-busy={loading}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <div className="h-4 w-32 rounded-full bg-white/70 mb-3" />
              <div className="h-10 w-72 rounded-lg bg-white/70" />
            </div>
            <div className="h-4 w-40 rounded-full bg-white/70" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] rounded-2xl bg-white/60" />
            ))}
          </div>
          {error && (
            <div className="mt-8 flex flex-col items-center gap-3 text-center" data-testid="categories-error">
              <p className="text-sm text-[#B4593F]">We couldn&apos;t load our collections. Please check your connection.</p>
              <button onClick={retry} className="inline-flex items-center gap-2 bg-[#B4593F] hover:bg-[#213a60] text-white px-6 py-3 rounded-full text-xs uppercase tracking-[0.2em] transition-colors" data-testid="categories-retry">Try again</button>
            </div>
          )}
        </div>
      </section>
    );
  }
  // Genuinely empty after load — hide the section entirely.
  if (!cats.length) return null;

  return (
    <section id="categories" ref={containerRef} className="relative py-10 md:py-24 bg-[#FDF3EB]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8 md:mb-14">
          <div className="reveal">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#B4593F] mb-3">{eyebrow}</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#213a60] leading-tight max-w-xl">{title}{titleAccent && <> <span className="italic">{titleAccent}</span></>}</h2>
            {subtitle && <p className="mt-4 text-neutral-600 max-w-lg text-sm">{subtitle}</p>}
          </div>
          <Link to="/shop" className="text-xs uppercase tracking-[0.2em] link-underline text-[#213a60] reveal">View All Collections</Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
          {cats.map((c, i) => (
            <Link
              key={c.id}
              to={c.comingSoon ? '#' : `/shop/${c.slug}`}
              className={`group relative reveal ${c.comingSoon ? 'pointer-events-none' : ''}`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#F5D0CE]/40 border border-[#EBD9CE]/60">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500" />
                {c.comingSoon && (
                  <div className="absolute top-3 left-3 bg-white/95 text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full text-[#213a60]">Coming Soon</div>
                )}
                {!c.comingSoon && (
                  <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-1 group-hover:translate-y-0">
                    <ArrowUpRight className="w-4 h-4 text-[#B4593F]" />
                  </div>
                )}
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="font-serif-display text-2xl leading-tight">{c.name}</div>
                  {c.tagline && c.tagline.trim().toLowerCase() !== 'coming soon' && (
                    <div className="text-[11px] uppercase tracking-[0.15em] opacity-90 mt-1">{c.tagline}</div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Categories;
